/**
 * The Ctrl+K search finding tasks from ANY page: one read of the open tasks, the first time the
 * search opens while the Tasks page has not loaded them (store action tasks/getTasksForSearch).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 *   searchRowsOf             Firestore docs → frozen task rows (raw fields; dueDayOf / rowMeta read them as they are)
 *   shouldLoadSearchTasks    should the shell start the read now?
 *   tasksSearchStatus        'ready' | 'failed' | 'loading' — what the search shows for tasks
 *
 * Nothing here starts a listener, shows the global loading overlay, or fills the Tasks page's own lists.
 */

/**
 * `docs`: Firestore QueryDocumentSnapshots (or anything with `id` + `data()`). Each row is the raw
 * document data plus `id`, `tags` always an array, and frozen (Vue skips frozen objects, so a few
 * hundred tasks cost no reactivity setup). A doc without an id is skipped.
 */
export function searchRowsOf(docs) {
  const out = [];
  (Array.isArray(docs) ? docs : []).forEach((d) => {
    if (!d || d.id === undefined || d.id === null || d.id === '' || typeof d.data !== 'function') return;
    const data = d.data() || {};
    out.push(Object.freeze({ ...data, id: d.id, tags: Array.isArray(data.tags) ? [...data.tags] : [] }));
  });
  return out;
}

/**
 * Start the read when: the search is open, the shell shows Tasks, we know the site, the Tasks page has
 * NOT loaded the tasks itself, a read is not already running, and this site has not been read already.
 * After a failure `loaded` is false again, so the next time the search opens it tries once more.
 * @param {{ open, canTasks, siteID, tasksLoaded, loading, loaded, loadedSiteID }} s
 */
export function shouldLoadSearchTasks(s) {
  const x = s || {};
  if (x.open !== true || x.canTasks !== true) return false;
  if (x.siteID === undefined || x.siteID === null || x.siteID === '') return false;
  if (x.tasksLoaded === true || x.loading === true) return false;
  return !(x.loaded === true && String(x.loadedSiteID) === String(x.siteID));
}

/**
 * What the search says about tasks. The Tasks page's own lists win (they are live); else the one-time
 * read; a failed read says so; otherwise it is still loading.
 * A read made for another site (the user switched sites) does not count.
 * @param {{ tasksLoaded, loaded, loadedSiteID, siteID, failed }} s
 */
export function tasksSearchStatus(s) {
  const x = s || {};
  const mine = x.loaded === true && String(x.loadedSiteID) === String(x.siteID);
  if (x.tasksLoaded === true || mine) return 'ready';
  if (x.failed === true) return 'failed';
  return 'loading';
}
